import { MomentInput } from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ProjectPhase } from 'api/project';
import { Sprint } from 'api/sprint-planning/types';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { Modal } from 'components/atoms/modal';
import { THRESHOLD_TASKS_NUMBER } from 'components/organisms/projects/project-plan/timeline/config/use-events-config';
import {
  filterTaskUpdates,
  handleDependencyConflicts,
} from 'components/organisms/projects/project-plan/timeline/helpers';
import { TaskConflictRadioOptions } from 'components/organisms/projects/project-plan/timeline/rearrange/date-conflict-modal';
import { PhasesTable } from 'components/organisms/projects/project-plan/timeline/rearrange/phases-table';
import { Conflict } from 'components/organisms/projects/project-plan/timeline/rearrange/types';
import {
  updateGanttDatesAction,
  updateGanttSlackAction,
  updateProjectPhaseDatesAction,
  updateProjectSprintDatesAction,
} from 'data/projects/actions';
import {
  updateProjectPhases,
  updateProjectSprints,
  updateTaskDates,
} from 'data/projects/sagas';
import { Task } from 'data/tasks';
import {
  updateTaskRecordSagaAction,
  updateTaskTimelineSagaAction,
} from 'data/tasks/actions';
import { DateShiftUpdates, Entity, EntityType, List } from 'utils/date-shift';

const ModalWrapper = styled.div`
  width: 664px;
  .ant-table.ant-table-small .ant-table-tbody > tr > td {
    padding: 0 !important;
  }
  .ant-table.ant-table-small .ant-table-thead > tr > th {
    padding: 0 var(--spacing-05);
  }
`;

const TextWrapper = styled.div`
  width: 70%;
  word-wrap: break-word;
  margin-bottom: var(--spacing-04);
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-05);
  align-items: center;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    margin-top: var(--spacing-05);
    border-top: 1px solid var(--coolest-gray-30);
    padding: 0 var(--spacing-05) var(--spacing-05);
  }
`;

const DependencyConflictModalContent = ({
  dataSource,
  startDateExtendedArray,
  dueDateExtendedArray,
  entity,
  isNewDependecy,
}) => {
  // comment
  return (
    <ModalWrapper>
      <TextWrapper>
        <FormattedMessage
          id={
            isNewDependecy
              ? 'task.dependency.conflicts.description.new_dependency'
              : 'task.dependency.conflicts.description'
          }
          values={{
            strong: (chunks) => <strong>{chunks}</strong>,
            taskName: entity.name,
          }}
        />
      </TextWrapper>

      <TextWrapper>
        <FormattedMessage id="task.dependency.conflicts.are_you_sure" />
      </TextWrapper>

      <PhasesTable
        dataSource={dataSource}
        startDateExtendedArray={startDateExtendedArray}
        dueDateExtendedArray={dueDateExtendedArray}
        dependencyConflict
        selected={TaskConflictRadioOptions.AutoAdjustTask}
      />
    </ModalWrapper>
  );
};

interface Props {
  visible: boolean;
  onClose: () => void;
  loading: boolean;
  projectId: number;
  taskUpdates: DateShiftUpdates;
  phaseUpdates: DateShiftUpdates;
  sprintUpdates: DateShiftUpdates;
  entity: Entity;
  showContent?: boolean;
  projectPhases: ProjectPhase[];
  tasks: Task[];
  sprints: Sprint[];
  onCancel?: () => void;
  onOk?: () => void;
  createTask?: boolean;
  taskList: List;
  sprintList: List;
  phaseList: List;
  setUpdateDatesDelay?: (_value: boolean) => void;
  setDependencyModalTimeout: (_value: boolean) => void;
  isNewDependecy?: boolean;
}

export const DependencyConflicModal: FC<Props> = ({
  visible,
  onClose,
  loading,
  projectId,
  taskUpdates,
  phaseUpdates,
  sprintUpdates,
  entity,
  showContent,
  taskList,
  sprintList,
  phaseList,
  tasks,
  projectPhases,
  sprints,
  onCancel,
  onOk,
  createTask,
  setUpdateDatesDelay,
  setDependencyModalTimeout,
  isNewDependecy = false,
}) => {
  const [startDateExtendedArray, setStartDateExtendedArray] = useState<
    number[]
  >([]);
  const [dueDateExtendedArray, setDueDateExtendedArray] = useState<number[]>(
    [],
  );
  const [dataSource, setDataSource] = useState<Conflict[]>([]);
  const [timeoutState, setTimeoutState] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (createTask && entity && entity.projectPhaseId && entity.name) {
      taskList[entity.id] = {
        hasStartDate: !!entity.startDate,
        hasDueDate: !!entity.dueDate,
        startDate: entity.startDate,
        dueDate: entity.dueDate,
        name: entity.name,
        depth: 1,
        id: entity.id,
        type: EntityType.Task,
        projectPhaseId: entity.projectPhaseId,
        parent: entity.projectPhaseId,
      };
      phaseList[entity.projectPhaseId] = {
        ...phaseList[entity.projectPhaseId],
        children: [
          ...(phaseList[entity.projectPhaseId].children || []),
          entity.id,
        ],
      };
    }
  }, [createTask, entity, phaseList, taskList]);

  const dispatch = useDispatch();

  const handleChange = () => {
    const payload: { [key: string]: MomentInput } = {};

    if (entity.startDate) {
      payload.startDate = entity.startDate;
    }
    if (entity.dueDate) {
      payload.dueDate = entity.dueDate;
    }
    const taskUpdatesLength = Object.keys(taskUpdates).length;

    if (taskUpdatesLength > THRESHOLD_TASKS_NUMBER && setUpdateDatesDelay) {
      setUpdateDatesDelay(true);
    }

    switch (entity.type) {
      case EntityType.Task:
        dispatch(
          updateTaskTimelineSagaAction({
            projectId,
            taskId: Number(entity.id),
            data: payload,
            options: {
              skipStoreUpdate: true,
              skipRefetch: true,
              isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
              toggleUpdating:
                taskUpdatesLength > THRESHOLD_TASKS_NUMBER &&
                setUpdateDatesDelay
                  ? setUpdateDatesDelay
                  : undefined,
            },
          }),
        );
        dispatch(
          updateTaskRecordSagaAction({
            projectId,
            taskId: Number(entity.id),
            data: payload,
          }),
        );
        break;
      case EntityType.Phase:
        if (entity.startDate && entity.dueDate) {
          dispatch(
            updateProjectPhaseDatesAction({
              data: {
                projectId,
                phaseId: Number(entity.id),
                startDate: entity.startDate as string,
                dueDate: entity.dueDate as string,
              },
              options: {
                isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
                toggleUpdating:
                  taskUpdatesLength > THRESHOLD_TASKS_NUMBER &&
                  setUpdateDatesDelay
                    ? setUpdateDatesDelay
                    : undefined,
              },
            }),
          );
          phaseUpdates[entity.id] = {
            id: entity.id,
            startDate: entity.startDate as string,
            dueDate: entity.dueDate as string,
          };
        }
        break;
      case EntityType.Sprint:
        if (entity.startDate && entity.dueDate) {
          dispatch(
            updateProjectSprintDatesAction({
              data: {
                sprintId: Number(entity.id),
                startDate: entity.startDate as string,
                dueDate: entity.dueDate as string,
              },
              options: {
                isSync: taskUpdatesLength > THRESHOLD_TASKS_NUMBER,
                toggleUpdating:
                  taskUpdatesLength > THRESHOLD_TASKS_NUMBER &&
                  setUpdateDatesDelay
                    ? setUpdateDatesDelay
                    : undefined,
              },
            }),
          );
        }
        break;
      default:
        break;
    }

    const filteredUpdates = filterTaskUpdates(taskUpdates, tasks);

    // have to do this to avoid a weird bug in gantt. We will be updating the record in the gantt at the end
    const updatestWithoutTask = Object.keys(filteredUpdates).reduce(
      (acc, key) => {
        if (key === entity.id.toString()) {
          return acc;
        }
        acc[key] = {
          ...filteredUpdates[key],
        };

        return acc;
      },
      {},
    );

    updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
    updateTaskDates(projectId, Object.values(filteredUpdates), queryClient);
    updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
    dispatch(
      updateGanttDatesAction({
        phaseUpdates,
        taskUpdates: updatestWithoutTask,
        phaseList,
        taskList,
        projectPhases,
        sprintUpdates,
        sprintList,
      }),
    );
    dispatch(updateGanttSlackAction({ projectId }));
    onClose();
  };

  useEffect(() => {
    if (
      phaseList &&
      taskList &&
      phaseUpdates &&
      taskUpdates &&
      tasks &&
      projectPhases
    ) {
      const conflicts = handleDependencyConflicts(
        phaseList,
        taskList,
        sprintList,
        phaseUpdates,
        taskUpdates,
        sprintUpdates,
        tasks,
        projectPhases,
        sprints,
      );

      if (conflicts && conflicts.length > 0) {
        setStartDateExtendedArray([]);
        setDueDateExtendedArray([]);

        conflicts.forEach((phase) => {
          const [phaseStartDate, phaseDueDate] = phase.currentDates;
          const [newPhaseStartDate, newPhaseDueDate] = phase.newDates;

          if (phaseStartDate !== newPhaseStartDate) {
            setStartDateExtendedArray((prev) => [...prev, phase.key]);
          }

          if (phaseDueDate !== newPhaseDueDate) {
            setDueDateExtendedArray((prev) => [...prev, phase.key]);
          }

          phase.children?.forEach((t) => {
            const [taskStartDate, taskDueDate] = t.currentDates;
            const [newTaskStartDate, newTaskDueDate] = t.newDates;

            if (taskStartDate !== newTaskStartDate) {
              setStartDateExtendedArray((prev) => [...prev, t.key]);
            }

            if (taskDueDate !== newTaskDueDate) {
              setDueDateExtendedArray((prev) => [...prev, t.key]);
            }
          });
        });

        // arrange the conflicts in the same order we get in project phases
        const arrangedConflicts = projectPhases
          .map((phase) => {
            const conflict = conflicts.find(
              (c) => c.key === phase.projectPhaseId,
            );

            return conflict;
          })
          .filter(Boolean);

        setDataSource(arrangedConflicts);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseList, taskList, phaseUpdates, taskUpdates, tasks, projectPhases]);

  if (showContent) {
    return (
      <DependencyConflictModalContent
        dataSource={dataSource}
        startDateExtendedArray={startDateExtendedArray}
        dueDateExtendedArray={dueDateExtendedArray}
        entity={entity}
        isNewDependecy={isNewDependecy}
      />
    );
  }

  const cancleHandler = (e) => {
    e.stopPropagation();
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const confirmHandler = (e) => {
    e.stopPropagation();
    if (onOk) {
      onOk();
    } else {
      handleChange();
    }

    if (setDependencyModalTimeout) {
      setDependencyModalTimeout(timeoutState);
    }
  };

  return (
    <StyledModal
      visible={visible}
      title={<FormattedMessage id="task.dependency.conflicts" />}
      onCancel={cancleHandler}
      onOk={confirmHandler}
      okText={<FormattedMessage id="confirm" />}
      destroyOnClose
      maskClosable={false}
      okButtonProps={{
        danger: true,
      }}
      width="696px"
      confirmLoading={loading}
      z-index={1000}
      footer={
        <FooterWrapper>
          <CheckboxWrapper>
            <Checkbox
              checked={timeoutState}
              onChange={(event) => {
                setTimeoutState(event.target.checked);
              }}
            >
              <FormattedMessage id="task.dependency.conflicts.disable.modal" />
            </Checkbox>
          </CheckboxWrapper>
          <ButtonWrapper>
            <Button onClick={cancleHandler}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button type="primary" onClick={confirmHandler}>
              <FormattedMessage id="confirm" />
            </Button>
          </ButtonWrapper>
        </FooterWrapper>
      }
    >
      <DependencyConflictModalContent
        dataSource={dataSource}
        startDateExtendedArray={startDateExtendedArray}
        dueDateExtendedArray={dueDateExtendedArray}
        entity={entity}
        isNewDependecy={isNewDependecy}
      />
    </StyledModal>
  );
};
